<template>
    <div class="sm:px-4">
        <div
            class="max-w-5xl bg-white dark:bg-neutral-800 md:rounded-lg shadow mx-auto overflow-hidden flex flex-col md:flex-row md:mb-12 min-h-[600px]">
            <div v-if="loading" class="flex justify-center items center w-full min-h-[600px]">
                <div
                    class="grid min-h-[140px] w-full place-items-center overflow-x-scroll rounded-lg p-6 lg:overflow-visible">
                    <svg class="text-gray-300 animate-spin" viewBox="0 0 64 64" fill="none"
                        xmlns="http://www.w3.org/2000/svg" width="24" height="24">
                        <path
                            d="M32 3C35.8083 3 39.5794 3.75011 43.0978 5.20749C46.6163 6.66488 49.8132 8.80101 52.5061 11.4939C55.199 14.1868 57.3351 17.3837 58.7925 20.9022C60.2499 24.4206 61 28.1917 61 32C61 35.8083 60.2499 39.5794 58.7925 43.0978C57.3351 46.6163 55.199 49.8132 52.5061 52.5061C49.8132 55.199 46.6163 57.3351 43.0978 58.7925C39.5794 60.2499 35.8083 61 32 61C28.1917 61 24.4206 60.2499 20.9022 58.7925C17.3837 57.3351 14.1868 55.199 11.4939 52.5061C8.801 49.8132 6.66487 46.6163 5.20749 43.0978C3.7501 39.5794 3 35.8083 3 32C3 28.1917 3.75011 24.4206 5.2075 20.9022C6.66489 17.3837 8.80101 14.1868 11.4939 11.4939C14.1868 8.80099 17.3838 6.66487 20.9022 5.20749C24.4206 3.7501 28.1917 3 32 3L32 3Z"
                            stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"></path>
                        <path
                            d="M32 3C36.5778 3 41.0906 4.08374 45.1692 6.16256C49.2477 8.24138 52.7762 11.2562 55.466 14.9605C58.1558 18.6647 59.9304 22.9531 60.6448 27.4748C61.3591 31.9965 60.9928 36.6232 59.5759 40.9762"
                            stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"
                            class="text-gray-900">
                        </path>
                    </svg>
                </div>
            </div>
            <template v-else>
                <div
                    class="lg:block w-full md:w-64 transform h-auto bg-white md:border-e border-gray-200 dark:bg-neutral-800 dark:border-neutral-700">
                    <div class="relative flex flex-row md:flex-col h-full max-h-full">
                        <!-- Header -->
                        <div class="p-4 hidden md:flex flex-col">
                            <router-link :to="{ name: 'dashboard' }" class="flex-none font-semibold text-xl text-black dark:text-white leading-6">
                                {{ $attrs.client?.name }}
                            </router-link>

                            <span v-if="$attrs.client?.bmb_owned" class="text-xs opacity-60 leading-4 dark:text-white">
                                By
                                <a :href="$BMB_API" class="hover:text-primary transition cursor-pointer"
                                    target="_blank">BoostmyBookings</a>
                            </span>

                            <span v-else class="text-xs opacity-60 leading-4 dark:text-white">
                                Powered by
                                <a :href="$BMB_API" class="hover:text-primary transition cursor-pointer"
                                    target="_blank">BoostmyBookings</a>
                            </span>
                        </div>

                        <!-- Sidebar Menu -->
                        <div
                            class="h-full overflow-y-auto bg-gray-100 sm:bg-gray-50 md:bg-white dark:bg-neutral-700 dark:md:bg-neutral-800 w-full">
                            <div class="pb-0 px-2 w-full flex md:flex-col md:flex-wrap">
                                <ul
                                    class="flex md:flex-col md:space-y-1 divide-x divide-gray-200 md:divide-none dark:divide-neutral-600">
                                    <li v-for="item in filteredMenu" :key="item.name">
                                        <router-link :to="getRoutePath(item)"
                                            class="w-full flex items-center gap-x-3.5 py-4 md:py-2 px-5 md:px-2.5 text-sm rounded-lg cursor-pointer"
                                            :class="$route.path.startsWith(getRoutePath(item))
                                                ? 'bg-gray-100 dark:bg-neutral-700 dark:text-white text-primary md:text-gray-800'
                                                : 'text-gray-800 hover:bg-gray-100 md:dark:bg-neutral-800 dark:hover:bg-neutral-700 dark:text-neutral-200'">
                                            <svg-vue :icon="item.icon" class="size-4" />
                                            {{ $t(item.name) }}
                                        </router-link>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Content -->
                <div class="min-h-96 bg-white dark:bg-neutral-800 w-full flex-1 min-w-0">
                    <div class="flex p-4 absolute hidden md:block" aria-label="Breadcrumb">
                        <ol class="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
                            <li class="inline-flex items-center">
                                <router-link :to="{ name: 'dashboard' }"
                                    class="inline-flex items-center text-sm font-medium text-gray-700 hover:text-primary dark:text-neutral-400 dark:hover:text-white cursor-pointer">
                                    <svg-vue icon="house" class="w-3 h-3 me-2.5" />
                                    {{ $t("Dashboard") }}
                                </router-link>
                            </li>

                            <li v-for="(crumb, index) in breadcrumb" :key="index">
                                <div class="flex items-center">
                                    <svg class="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1"
                                        xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
                                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
                                            stroke-width="2" d="m1 9 4-4-4-4" />
                                    </svg>

                                    <router-link :to="crumb.path"
                                        class="ms-1 text-sm font-medium text-gray-700 md:ms-2 dark:text-neutral-400 hover:text-primary dark:hover:text-white">
                                        {{ $t(crumb.name) }}
                                    </router-link>
                                </div>
                            </li>
                        </ol>
                    </div>

                    <div class="w-full h-full">
                        <router-view :access-types="accessTypes" :client="$attrs.client" @update-name="setName"/>
                        <div v-if="$route.name === null">
                            <p>No child route selected</p>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import dashboardHelpers from "./dashboard-helpers";

export default {
    data() {
        return {
            loading: true,
            accessTypes: [],
            currentName: '',
            menuItems: [
                { name: "Account", icon: "users", resource: null },
                { name: "Locations", icon: "location", resource: "App\\Models\\Location" },
                { name: "Events", icon: "event", resource: "App\\Models\\Event" },
                { name: "Promotions", icon: "crown", resource: null },
            ],
        };
    },
    computed: {
        filteredMenu() {
            return this.menuItems.filter(item => {
                // Always show Account
                if (!item.resource && item.name === "Account") return true;

                // Normal case: show only if accessTypes includes the resource
                return !item.resource || this.accessTypes.includes(item.resource);
            });
        },
        pageTitle() {
            const path = this.$route.path.split("/").pop();
            const found = this.menuItems.find(
                (item) => item.name.toLowerCase() === path
            );
            return found ? found.name : "Account";
        },
        breadcrumb() {
            let path = this.$route.path;
            const segments = path.split('/').filter(Boolean);
            let startIndex = 0;

            const defaultLocale = 'en';
            const supportedLocales = ['nl', 'es', 'fr', 'de'];

            if (supportedLocales.includes(segments[0])) startIndex = 1;
            if (segments[startIndex] === 'dashboard') startIndex++;

            let pathAccumulator = '/dashboard';
            let crumbs = segments.slice(startIndex).map(part => {
                pathAccumulator += `/${part}`;
                const route = this.$router.match(pathAccumulator);

                return {
                name: route.meta.title || part.charAt(0).toUpperCase() + part.slice(1),
                path: pathAccumulator
                };
            });

            // --- 🧠 Detect if last segment is dynamic (numeric ID or slug) ---
            const lastSegment = segments[segments.length - 1];
            const isDynamic =
                lastSegment &&
                (/\d+$/.test(lastSegment) || /^[a-z0-9-]{6,}$/i.test(lastSegment));

            if (isDynamic && crumbs.length > 1) {
                // Remove the dynamic last crumb
                crumbs.pop();
                // Rename the new last one
                const prev = crumbs[crumbs.length - 1];
                prev.name = `Edit ${this.currentName || ''}`;
            }

            return crumbs;
        }
    },
    methods: {
        ...dashboardHelpers,

        getRoutePath(item) {
            const base = "/dashboard";
            const slug = item.name.toLowerCase();
            return slug === "account" ? `${base}/account` : `${base}/${slug}`;
        },
        setName(name) {
            this.currentName = name;
        },

        async initSessionAndLoadAccess() {
            await axios.get("/sanctum/csrf-cookie");
            const res = await axios.get("/api/dashboard/access-types", {
                withCredentials: true,
            });
            this.accessTypes = res.data;
            this.loading = false;

            // 🚨 Redirect if current page not allowed
            const current = this.menuItems.find(
                (i) => this.$route.path.includes(i.name.toLowerCase())
            );
            if (current?.resource && !this.accessTypes.includes(current.resource)) {
                this.$router.replace("/dashboard/account");
            }
        },
    },
    mounted() {
        this.initSessionAndLoadAccess();
    },
};
</script>